import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    ICheckpointCondition,
    ICheckpointConditionTypeOption,
    ICheckpointRange,
} from '@fi-shared/checklist/checklist-v2';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import {
    FiTooltipButtonIconColors,
    FiTooltipIconClassType,
} from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { ConditionTargetTypeOptions } from '@fi-shared/checklist/checklist-consts';
import {
    CheckpointConditionTargetType,
    CheckpointConditionType,
    CheckpointEditorFormModelNames,
    CheckpointType,
} from '@fi-shared/checklist/checklist-enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';

@Component({
    selector: 'fi-checkpoint-condition',
    templateUrl: './condition.component.html',
    styleUrls: ['./condition.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-condition' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiCheckpointConditionComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiCheckpointConditionComponent,
        },
    ],
    standalone: false,
})
export class FiCheckpointConditionComponent
    extends ControlValueAccessorValidatorBase<ICheckpointCondition>
    implements OnInit, OnDestroy, OnChanges
{
    @Input() checkpointRanges: ICheckpointRange[] = [];
    @Input() checkpointType: CheckpointType;
    @Output() onDeleteConditionClick = new EventEmitter<void>();
    @Input() updateConditionTypeOptions$ = new Subject<void>();
    @Input() checkpointConditions: ICheckpointCondition[] = [];
    @Input() conditionIndex: number;
    @Input() isQRCodeEnabled: boolean;

    ConditionTargetTypeOptions = ConditionTargetTypeOptions;
    FiTooltipButtonIconColors = FiTooltipButtonIconColors;
    conditionTypeOptions: ICheckpointConditionTypeOption[] = [];
    conditionTypeQRCodeOptions: ICheckpointConditionTypeOption[];

    get condition(): ICheckpointCondition {
        return this.ngModel;
    }

    constructor(
        injector: Injector,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.conditionTypeQRCodeOptions = this.fiCheckpoint2HelperService.getCheckpointConditionTypeQRCodeOptions();
        this.setConditionTypeOptions();
        this.subscribeOnConditionTypeUpdateTrigger();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.checkpointType || changes.checkpointRanges) {
            this.setConditionTypeOptions();
            this.cdr.detectChanges();
        }
    }

    private subscribeOnConditionTypeUpdateTrigger() {
        this.updateConditionTypeOptions$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.setConditionTypeOptions();
            this.cdr.detectChanges();
        });
    }

    private setConditionTypeOptions() {
        this.conditionTypeOptions = this.fiCheckpoint2HelperService.getCheckpointConditionTypeOptions(
            this.checkpointType,
            this.checkpointRanges,
        );
    }

    getConditionTypeOptions(conditionTargetType: CheckpointConditionTargetType): ICheckpointConditionTypeOption[] {
        if (conditionTargetType === CheckpointConditionTargetType.QRCode) {
            return this.conditionTypeQRCodeOptions;
        }

        return this.conditionTypeOptions;
    }

    onRangeRemoveClick() {
        this.onDeleteConditionClick.emit();
    }

    onConditionTargetTypeChanged(condition: ICheckpointCondition) {
        if (condition.TargetType === CheckpointConditionTargetType.QRCode) {
            condition.Type = CheckpointConditionType.Always;
        }
    }

    onConditionTypeChange(selectedOption: ICheckpointConditionTypeOption) {}

    isConditionTypeDisabled(conditionTypeOptionId: number): boolean {
        if (this.isTargetTypeDisabled(this.condition.TargetType)) return true;

        const conditionsWithSameTargetType = this.checkpointConditions.filter((condition, index) => {
            return condition.TargetType === this.condition.TargetType && index !== this.conditionIndex;
        });
        return conditionsWithSameTargetType.some((condition) => condition.Type === conditionTypeOptionId);
    }

    isTargetTypeDisabled(conditionTargetType: CheckpointConditionTargetType): boolean {
        return this.fiCheckpoint2HelperService.isConditionDisabledByQRCode(conditionTargetType, this.isQRCodeEnabled);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly CheckpointEditorFormModelNames = CheckpointEditorFormModelNames;
    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
}
